import config from "../../app.json";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { loginSuccesstrackGoogle, signUpSuccesstrackGoogle } from "../../Utils/GoogleAnalytics";

import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
} from "firebase/auth";

import { Context } from "../../Reducers/Store";
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import FieldText from "../../Components/Fields/FieldText";
import { Row, Col, Button } from "react-bootstrap";
import AlertComponent from "../../Components/AlertComponent";
import LeftLogo from "../../Assets/Svgs/ai-image.svg";
import ChimpLogin from "../../Assets/Svgs/dark-logo.svg";
import Image from "../../Components/Image/image";
import GoogleButton from "react-google-button";

function Login() {
  const [state, dispatch] = useContext(Context);
  const [email, setEmail] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [name, setName] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [password, setPassword] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [recaptcha, setRecaptcha] = useState(null);
  const [activeOption, setActiveOption] = useState("signin");
  const [disableClass, setDisableClass] = useState("");
  const [validatedRecaptcha, setValidatedRecaptcha] = useState(false);

  const handleOptionSelect = (opt) => {
    setActiveOption(opt);
  };

  const auth = getAuth();
  setPersistence(auth, browserLocalPersistence);

  let location = useLocation();
  let navigate = useNavigate();
  let from = location.state?.from?.pathname || "/";

  useEffect(() => {
    const disabledClass = !config.useRecaptcha || (recaptcha && activeOption === "create") || activeOption === "signin" ? "" : "disabled";
    setDisableClass(disabledClass);
  }, [activeOption, recaptcha]);

  useEffect(() => {
    if (validatedRecaptcha) {
      createAccount();
    }
  }, [validatedRecaptcha]);

  useEffect(() => {
    if (state.isLogged) {
      dispatch({ type: "SET_LOADING", payload: true });

      axios
        .get(config.API_HM + "/profile")
        .then((result) => {
          const payload = { ...result.data };
          dispatch({ type: "SET_PROFILE", payload: payload });
        })
        .catch((error) => {
          AlertComponent("Error", error.message ?? "Something went wrong. Profile could not be loaded. Code: 10", { icon: `error` });
        })
        .finally(() => {
          dispatch({ type: "SET_LOADING", payload: false });
        });
    }
  }, [state.isLogged]);

  useEffect(() => {
    if (state.profile) {
      navigate(from || state?.defaultPath, { replace: false });
    }
  }, [state.profile]);

  const AuthWithEmailAndPassword = () => {
    if (email.includes("socialchimp.com")) {
      return AlertComponent("Error", "Something went wrong. Try again in a few minutes.", { icon: `error` });
    }

    signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        loginSuccesstrackGoogle();
        if (userCredential?.user?.accessToken) {
          dispatch({ type: "SET_LOGGED", payload: { token: userCredential.user.accessToken, refreshToken: userCredential.user.refreshToken } });
        } else {
          return AlertComponent("Error", "Username or password is invalid.", { icon: `error` });
        }
      })
      .catch((error) => {
        if (error.code == "auth/user-not-found" || error.code == "auth/wrong-password") {
          return AlertComponent("Invalid Credentials", "Please, check your email and password.", { icon: `error` });
        } else {
          return AlertComponent("Error", error.code + " - " + error.message, {
            icon: `error`,
          });
        }
      });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (activeOption === "signin") {
      signin();
    } else {
      checkRecaptcha();
    }
  };

  const checkRecaptcha = async () => {
    if (!email) setEmailError("E-mail is required");
    if (!name) setNameError("Name is required");
    else if (!password) setPasswordError("Password is required");
    else {
      if (config.useRecaptcha) {
        try {
          const data = {token : recaptcha,userData : {
            email : email,
            name : name
          }}
          axios
            .post(config.API_HM + "/Recaptcha/verify", data, {
              headers: {
                "Content-type": "application/json",
              },
            })
            .then((res) => {
              setValidatedRecaptcha(res.data.success);
            })
            .catch(function (error) {
              return AlertComponent(
                "Error",
                error?.response?.data?.message || error?.response?.data?.errors?.Brand[0] || error?.response?.data?.errors?.User[0] || error?.message,
                { icon: `error` }
              );
            });
        } catch (error) {
          setValidatedRecaptcha(false);
          setRecaptcha(null);
          return AlertComponent("Error", error.message || "Something went wrong. Try again in a few minutes.", { icon: `error` });
        }
      } else {
        setValidatedRecaptcha(true);
      }
    }
  };
  const createAccount = async (isGoogle = false) => {
    try {
      // 🔥 Google Sign-in: Skip all validations and directly open the Google popup
      if (isGoogle) {
        const auth = getAuth();
        const provider = new GoogleAuthProvider();
  
        await signInWithPopup(auth, provider)
          .then(async (userCredential) => {
            const user = userCredential.user;
  
            // Save the Google user in externalUsers collection
            await axios.post(`${config.API_HM}/users/external-users`, {
              email: user.email,
              name: user.displayName,
              uid: user.uid,
              provider: "google",
            });
  
            AlertComponent("Success", "Google account linked successfully.");
            dispatch({
              type: "SET_LOGGED",
              payload: { token: user.accessToken, refreshToken: user.refreshToken }
            });
  
            navigate(from || state?.defaultPath, { replace: false });
          })
          .catch((error) => {
            console.error(error);
            AlertComponent("Error", "Failed to sign in with Google.", { icon: `error` });
          });
  
        return;  // ✅ Exit function after handling Google sign-in
      }
  
      // 🔥 Email/Password Sign-up Flow
      if (!email) {
        setEmailError("E-mail is required");
        return;
      }
  
      if (!name) {
        setNameError("Name is required");
        return;
      }
  
      if (!password) {
        setPasswordError("Password is required");
        return;
      }
  
      // 🛠️ Prevent `socialchimp.com` emails
      if (email.includes("socialchimp.com")) {
        return AlertComponent("Error", "Something went wrong. Try again in a few minutes.", { icon: `error` });
      }
  
      // ✅ Create account with Email/Password
      await createUserWithEmailAndPassword(getAuth(), email, password)
        .then(async (response) => {
          if (response.user) {
            await updateProfile(response.user, { displayName: name });
  
            // Save regular user in users collection
            await axios.post(`${config.API_HM}/users`, {
              email: email,
              name: name,
              uid: response.user.uid,
              provider: "email",
            });
  
            AlertComponent("Success", "Account created successfully.");
            signin();
          }
        })
        .catch((error) => {
          console.error(error);
          AlertComponent("Error", "Account creation failed.", { icon: `error` });
        });
  
    } catch (error) {
      console.error(error);
      AlertComponent("Error", error.message || "Something went wrong. Try again in a few minutes.", { icon: `error` });
    }
  };
  

  const signin = () => {
    if (!email) setEmailError("E-mail is required");
    else if (!password) setPasswordError("Password is required");
    else {
      setEmailError(null);
      setPasswordError(null);

      if (email == null || email == "" || password == null || password == "") {
        return AlertComponent("Error", "You must inform your email and password.", "OK", null);
      }

      AuthWithEmailAndPassword();
    }
  };

  // Currently even when the user has no account if he tries to login with google, it works normally
  // this function will check the creation and during the handle Google Sign In, if it was create 5 segonds ago
  // and the user is not in the 'create' tab, it will delete the account
  // I'm commenting this section but it's already tested if Filipe needs to enable it again.
  const checkCreationDate = (epochDate) => {
    const currentDate = new Date();
    const epochDateObj = new Date(parseInt(epochDate));
    const differenceInSeconds = (currentDate - epochDateObj) / 1000;
    return differenceInSeconds <= 5;
  };

  const handleGoogleSignIn = async () => {
    createAccount(true)
    // const provider = new GoogleAuthProvider();
    // try {
    //   await signInWithPopup(auth, provider).then((userCredential) => {
    //     const isNewUser = checkCreationDate(userCredential?.user?.metadata?.createdAt) ?? false;

    //     if (isNewUser) {
    //       signUpSuccesstrackGoogle(true);
    //     }
    //     // if (isNewUser && activeOption !== "create") {
    //     //   userCredential.user.delete();
    //     //   return AlertComponent("Error", "Account does not exist.", { icon: `error` });
    //     // }
    //     if (userCredential?.user?.email.includes("socialchimp.com")) {
    //       return AlertComponent("Error", "Something went wrong. Try again in a few minutes.", { icon: `error` });
    //     }
    //     if (userCredential?.user?.accessToken) {
    //       loginSuccesstrackGoogle();
    //       dispatch({
    //         type: "SET_LOGGED",
    //         payload: { token: userCredential.user.accessToken, refreshToken: userCredential.user.refreshToken },
    //       });
    //     } else {
    //       return AlertComponent("Error", "Username or password is invalid.", { icon: `error` });
    //     }
    //   });
    // } catch (error) {
    //   if (error?.message !== "Firebase: Error (auth/cancelled-popup-request).") {
    //     return AlertComponent("Error", error?.data?.message || error?.message, { icon: `error` });
    //   }
    // }
  };

  const onRecaptchaChange = (value) => {
    setRecaptcha(value);
  };

  return (
    <div className="relative">
      <div className="button-wrapper">
  <a href="/welcome">
    <button className="back-button custom-hover bg-dark text-white border-light">Get started</button>
  </a>
</div>
    <div className="containerLoginForm">
      
      <Row className="login-body">
      <Col md={5}>
        

        <div className="containerLoginImage">
          <h2>Unlock the power of AI content with SocialChimp</h2>
          <h4>
            Chat with the smartest AI - Experience the power of AI with custom social generated content for all your needs.
          </h4>
          <img resizeMode="cover" src={LeftLogo} className="left-image-login" />
        </div>
      </Col>

        <Col md={7}>
          <div className="main-content">
            <div className="login-container">
              <Row style={{ justifyContent: "center" }}>
                <Image src={ChimpLogin} width={200} height={50} />
              </Row>
              <Row>
                <div className="login-options">
                  <Button variant="default" onClick={() => handleOptionSelect("signin")} className={`${activeOption === `signin` ? `active` : ``}`}>
                    Sign In
                  </Button>
                  <Button variant="default" onClick={() => handleOptionSelect("create")} className={`${activeOption === `create` ? `active` : ``}`}>
                    Create account
                  </Button>
                </div>
              </Row>
              <Row>
                <Col md={12} className="login-button-container">
                  <GoogleButton label="Continue with Google" type="light" className="googlesignin" onClick={handleGoogleSignIn} />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="divider-login">
                    <div className="line-login"></div>
                    <h5>OR</h5>
                    <div className="line-login"></div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="cs-login-form">
                    <form onSubmit={handleLogin} className="form-login">
                      {activeOption == "create" && (
                        <Row>
                          <Col className="col-md-12">
                            <FieldText
                              label="Full Name"
                              type="name"
                              onChange={(x) => setName(x.target.value)}
                              placeholder="Full Name"
                              value={name}
                              className="name-field"
                              error={nameError}
                              required={true}
                              inputClassName={`inputOption name-input`}
                              autoComplete="off"
                            />
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col className="col-md-12">
                          <FieldText
                            label="Email"
                            type="email"
                            onChange={(x) => setEmail(x.target.value)}
                            placeholder="Email"
                            value={email}
                            className="email-field"
                            error={emailError}
                            required={true}
                            inputClassName={`inputOption email-input`}
                            autoComplete="off"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col className="col-md-12">
                          <FieldText
                            label="Password"
                            type="password"
                            placeholder="Password"
                            onChange={(x) => setPassword(x.target.value)}
                            value={password}
                            className="password-field"
                            error={passwordError}
                            required={true}
                            inputClassName={`inputOption password-input`}
                            autoComplete="off"
                          />
                          <div className="forgot-pass-link">
                            <span className="signUpText">
                              {activeOption === "signin" ? (
                                <a className="signUpLink" onClick={() => navigate("/recoverpassword", { replace: false })}>
                                  Forgot Password?
                                </a>
                              ) : null}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      {config.useRecaptcha && activeOption == "create" && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <ReCAPTCHA sitekey="6Lcg6lMoAAAAAJTQNNvY6QKsUN30v7F3Cb1zGR1J" onChange={onRecaptchaChange} />
                        </div>
                      )}
                      <div className="form-group loginAlignCenter mt-3">
                        <button type="submit" className={`btn submitLogin col-md-12 ${disableClass}`}>
                          {activeOption === "signin" ? `Sign In` : `Create Account`}
                        </button>
                      </div>
                      {activeOption !== "signin" && (
                        <div className="login-link">
                          By creating an account, you agree to our{" "}
                          <a href="/terms-of-service" target="_blank" style={{ cursor: "pointer" }}>
                            Terms of Service
                          </a>{" "}
                          and
                          <a href="/privacy-and-cookie-statement" target="_blank" style={{ cursor: "pointer" }}>
                            {" "}
                            Privacy & Cookie Statement
                          </a>
                          .
                        </div>
                      )}
                    </form>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
    </div>
    </div>
  );
}

export default Login;
